import { CheckIcon } from "lucide-react-native";
import React, { memo, ReactNode } from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Icon } from "@/components/atoms/Icon";
import {
  Popover,
  PopoverBackdrop,
  PopoverContent,
} from "@/components/atoms/Popover";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";

import MenuItem from "@/components/organisms/shared/MenuItem";

import { IMenuSelection } from "@/stores/selectionSort";

// design-system: dynamic-style waiver — safe-area bottom inset (measured,
// runtime) applied through the ScrollView atom's contentContainerStyle, the
// sanctioned passthrough for native list/scroll geometry (no inline literal).
const sortPopoverContentInsetStyle = (bottomInset: number) => ({
  paddingBottom: bottomInset,
});

/**
 * Props for the SelectionSortPopoverView component
 */
export interface SelectionSortPopoverViewProps {
  readonly menuSelectionSortList: IMenuSelection[];
  readonly showSetting?: IMenuSelection;
  /** Whether descending sort is currently selected */
  readonly isDescendingSelected: boolean;
  /** Whether ascending sort is currently selected */
  readonly isAscendingSelected: boolean;
  readonly isOpenPopover?: boolean;
  readonly isSortPlayerEnable?: boolean;
  readonly isSmallScreen: boolean;
  readonly isDark: boolean;
  /** Bottom safe area inset value in pixels, used for padding on devices with notches */
  readonly bottomInset: number;
  /** Pre-memoized render function for the popover trigger button */
  readonly renderTrigger: (triggerProps: Record<string, unknown>) => ReactNode;
  /** Record of pre-memoized handlers for each menu item, keyed by menu type */
  readonly menuItemHandlers: Record<string, () => void>;

  readonly onClosePopover?: () => void;
  readonly onPressSortOrderAsc: () => void;
  readonly onPressSortOrderDesc: () => void;
}

/**
 * View component that renders a popover with sorting and selection options
 * @param root0 - Component props
 * @param root0.menuSelectionSortList - Array of available sort options to display in the popover
 * @param root0.showSetting - Currently selected display/sort option
 * @param root0.isDescendingSelected - Whether descending sort is currently selected
 * @param root0.isAscendingSelected - Whether ascending sort is currently selected
 * @param root0.isOpenPopover - Boolean flag indicating if the popover is currently visible
 * @param root0.isSortPlayerEnable - Boolean flag indicating if player sorting feature is enabled
 * @param root0.isSmallScreen - Boolean flag indicating if the device has a small screen
 * @param root0.onClosePopover - Callback function invoked when the popover is closed
 * @param root0.onPressSortOrderAsc - Pre-memoized callback for ascending sort
 * @param root0.onPressSortOrderDesc - Pre-memoized callback for descending sort
 * @param root0.isDark - Boolean flag indicating if dark mode is active
 * @param root0.bottomInset - Bottom safe area inset value for padding on devices with notches
 * @param root0.renderTrigger - Pre-memoized render function for popover trigger
 * @param root0.menuItemHandlers - Record of pre-memoized handlers keyed by menu type
 */
const SelectionSortPopoverView = ({
  menuSelectionSortList,
  showSetting,
  isDescendingSelected,
  isAscendingSelected,
  isOpenPopover,
  isSortPlayerEnable,
  isSmallScreen,
  onClosePopover,
  onPressSortOrderAsc,
  onPressSortOrderDesc,
  isDark,
  bottomInset,
  renderTrigger,
  menuItemHandlers,
}: SelectionSortPopoverViewProps) => (
  <Popover
    placement="bottom"
    offset={8}
    crossOffset={Platform.OS === "android" ? 0 : 16}
    size="lg"
    trigger={renderTrigger}
    useRNModal
    isOpen={isOpenPopover}
    onClose={onClosePopover}
  >
    <PopoverBackdrop />
    <PopoverContent
      className={`min-w-40 flex-1 rounded-md border-0 p-0 -translate-x-6 ${isDark ? "bg-surface-action" : "bg-surface-raised"}`}
    >
      <Box className="web:flex-1 native:size-full">
        <ScrollView
          contentContainerClassName="flex-grow"
          contentContainerStyle={sortPopoverContentInsetStyle(bottomInset)}
        >
          {menuSelectionSortList.map((menu: IMenuSelection, index: number) => (
            <MenuItem
              key={`menu-${index}`}
              menu={menu}
              index={index}
              isSelected={showSetting?.type === menu.type}
              isSmallScreen={isSmallScreen}
              onPress={menuItemHandlers[menu.type]}
            />
          ))}
        </ScrollView>
        {isSortPlayerEnable && (
          <>
            <Divider
              className={`h-2 ${isDark ? "bg-typography-black" : "bg-surface-strong"}`}
            />
            <Pressable
              className={`flex-row items-center gap-3 ${!isSmallScreen ? "px-3" : "px-4"} py-2`}
              onPress={onPressSortOrderDesc}
            >
              <Box className="w-4">
                {isDescendingSelected && <Icon as={CheckIcon} />}
              </Box>
              <Box className="flex-1">
                <Text
                  variant="unstyled"
                  className={`font-medium ${!isSmallScreen ? "text-title-sm" : "text-title"}`}
                >
                  Descending
                </Text>
              </Box>
            </Pressable>
            <Pressable
              className={`flex-row items-center gap-3 border-t border-surface-muted ${!isSmallScreen ? "px-3" : "px-4"} py-2`}
              onPress={onPressSortOrderAsc}
            >
              <Box className="w-4">
                {isAscendingSelected && <Icon as={CheckIcon} />}
              </Box>
              <Box className="flex-1">
                <Text
                  variant="unstyled"
                  className={`font-medium ${!isSmallScreen ? "text-title-sm" : "text-title"}`}
                >
                  Ascending
                </Text>
              </Box>
            </Pressable>
          </>
        )}
      </Box>
    </PopoverContent>
  </Popover>
);

SelectionSortPopoverView.displayName = "SelectionSortPopoverView";

export default memo(SelectionSortPopoverView);
